// src/context/AuthContext.js
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { jwtDecode } from "jwt-decode"; // Именованный импорт
import { SERVER_BASE_URL } from "../config";

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [organizer, setOrganizer] = useState(null); // Данные организатора для работников

  // Функция проверки валидности токена
  const checkTokenValidity = useCallback((token) => {
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      // exp хранится в секундах, поэтому умножаем на 1000 для сравнения с Date.now()
      if (decoded.exp * 1000 < Date.now()) {
        localStorage.removeItem("token");
        return false;
      }
      return true;
    } catch (error) {
      localStorage.removeItem("token");
      return false;
    }
  }, []);

  // Функция для получения данных организатора
  const fetchOrganizerData = useCallback(async (organizerId, token) => {
    if (!organizerId || !token) return null;
    try {
      const res = await fetch(`${SERVER_BASE_URL}/api/users/${organizerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (error) {
      console.error('Error fetching organizer data:', error);
      return null;
    }
  }, []);

  // Функция для получения данных пользователя с бэкенда
  const fetchUserData = useCallback(async (token) => {
    if (!token) return null;
    try {
      const res = await fetch(`${SERVER_BASE_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        localStorage.removeItem("token");
        return null;
      }
      const userData = await res.json();
      
      // Если пользователь - работник, загружаем данные организатора
      if (userData.role === 'worker' && userData.owner_id) {
        const organizerData = await fetchOrganizerData(userData.owner_id, token);
        if (organizerData) {
          setOrganizer(organizerData);
          // console.log('Organizer data loaded for worker:', organizerData);
        }
      } else {
        setOrganizer(null); // Очищаем данные организатора для не-работников
      }
      
      return userData;
    } catch (error) {
      localStorage.removeItem("token");
      return null;
    }
  }, [fetchOrganizerData]);

  // Инициализация авторизации при монтировании провайдера
  useEffect(() => {
    async function initAuth() {
      const token = localStorage.getItem("token");
      if (!checkTokenValidity(token)) {
        setUser(null);
        setIsAuthenticated(false);
        setOrganizer(null);
        setIsInitializing(false);
        return;
      }

      const userData = await fetchUserData(token);
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setOrganizer(null);
      }
      setIsInitializing(false);
    }
    initAuth();
  }, [checkTokenValidity, fetchUserData]);

  // Функция логина
  const login = useCallback(
    async (tokenFromServer) => {
      localStorage.setItem("token", tokenFromServer);

      if (!checkTokenValidity(tokenFromServer)) {
        setUser(null);
        setIsAuthenticated(false);
        setOrganizer(null);
        return;
      }

      const userData = await fetchUserData(tokenFromServer);
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setOrganizer(null);
      }
    },
    [checkTokenValidity, fetchUserData]
  );

  // Функция логаута
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setIsAuthenticated(false);
    setUser(null);
    setOrganizer(null);
  }, []);

  // Мемоизируем объект value для предотвращения лишних ререндеров
  const contextValue = useMemo(
    () => ({
      isInitializing,
      isAuthenticated,
      user,
      organizer, // Добавляем данные организатора в контекст
      setUser,
      login,
      logout,
    }),
    [isInitializing, isAuthenticated, user, organizer, login, logout]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};